import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { NumericInput } from "@/components/ui/numeric-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Tag, AlertCircle, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";

export interface Pro017StepTwoData {
  discountValue: number;
  discountType: number;
}

interface StepTwoProps {
  data: Pro017StepTwoData;
  updateData: (data: Partial<Pro017StepTwoData>) => void;
  nextStep: () => void;
  prevStep: () => void;
}

const discountTypes = [
  { value: 0, label: "R$ - Valor fixo de desconto" },
  { value: 1, label: "% - Desconto percentual" },
];

export default function StepTwo({ data, updateData, nextStep, prevStep }: StepTwoProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isFormValid = useMemo(() => {
    const hasValidDiscountType = [0, 1].includes(data.discountType);
    // PRO017: valor deve ser > 0 (não faz sentido desconto de R$0 ou 0%)
    const hasValidDiscountValue = data.discountValue > 0;
    return hasValidDiscountType && hasValidDiscountValue;
  }, [data.discountType, data.discountValue]);

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    // PRO017: valor deve ser > 0 (não faz sentido desconto de R$0 ou 0%)
    if (data.discountValue <= 0) {
      newErrors.discountValue = data.discountType === 0
        ? "O valor do desconto deve ser maior que R$ 0,00"
        : "O percentual de desconto deve ser maior que 0%";
    }

    // Tipo de desconto válido (0 ou 1)
    if (![0, 1].includes(data.discountType)) {
      newErrors.discountType = "Tipo de desconto inválido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      nextStep();
    } else {
      toast({
        variant: "destructive",
        title: "Erro de validação",
        description: "Verifique os campos obrigatórios antes de continuar.",
      });
    }
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      <div className="bg-card rounded-lg border p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-primary" />
          <div>
            <h3 className="text-base font-semibold">
              Configuração de Desconto *
            </h3>
            <p className="text-sm text-muted-foreground">
              Defina o desconto aplicado ao comprar um item de cada lista
            </p>
          </div>
        </div>

        {/* Tipo de Desconto */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="discountType" className="text-sm font-medium">
              Tipo de Desconto *
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div className="space-y-2 text-xs">
                    <div>
                      <p className="font-semibold">R$ - Valor Fixo:</p>
                      <p>Desconto de valor fixo em reais</p>
                      <p className="text-muted-foreground">Ex: R$ 20,00 de desconto</p>
                    </div>
                    <div>
                      <p className="font-semibold">% - Percentual:</p>
                      <p>Desconto percentual sobre o valor total</p>
                      <p className="text-muted-foreground">Ex: 15% de desconto</p>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select
            value={data.discountType?.toString()}
            onValueChange={(value) => {
              updateData({ discountType: parseInt(value) });
              setErrors({ ...errors, discountType: "" });
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Selecione o tipo de desconto" />
            </SelectTrigger>
            <SelectContent>
              {discountTypes.map((type) => (
                <SelectItem key={type.value} value={type.value.toString()}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.discountType && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.discountType}
            </p>
          )}
        </div>

        {/* Valor/Percentual */}
        <div className="space-y-2">
          <Label htmlFor="discountValue" className="text-sm font-medium">
            {data.discountType === 0 ? "Valor do Desconto (R$)" : "Percentual do Desconto (%)"} *
          </Label>
          <NumericInput
            id="discountValue"
            value={data.discountValue}
            onChange={(val) => {
              updateData({ discountValue: val });
              setErrors({ ...errors, discountValue: "" });
            }}
            min={0}
            max={data.discountType === 1 ? 100 : 9999.99}
            maxLength={data.discountType === 1 ? 3 : 7}
            step={0.01}
            numberType="decimal"
            placeholder={data.discountType === 0 ? "Ex: 20.00" : "Ex: 15"}
            hasError={!!errors.discountValue}
          />
          <p className="text-xs text-muted-foreground">
            {data.discountType === 0
              ? "Valor fixo em reais (ex: 20.00 para R$ 20,00)"
              : "Percentual sem o símbolo % (ex: 15 para 15%)"}
          </p>
          {errors.discountValue && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.discountValue}
            </p>
          )}
        </div>

        {/* Informação sobre aplicação do desconto */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="text-xs text-blue-900">
              <p className="font-semibold mb-1">Como funciona o combo:</p>
              <p>
                O cliente precisa comprar <strong>um item de cada lista</strong> para receber o desconto.
                Exemplo: 1 mochila (Lista 1) + 1 estojo (Lista 2) + 1 caderno (Lista 3) = desconto aplicado.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          onClick={prevStep}
          className="w-full md:w-auto"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isFormValid}
          className="w-full md:w-auto"
        >
          Continuar
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
